import { ChevronDown, ChevronLeft, ChevronRight, Search, X } from 'lucide-react'
import type { KeyboardEvent } from 'react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { ScrollArea } from '@/components/ui/scroll-area'
import type { TargetMarket } from '@/lib/targetMarkets.types'
import { cn } from '@/lib/utils'
import type {
  TargetMarketSelectProps,
  TargetMarketTab,
  TargetMarketTabOption,
} from './TargetMarketSelect.types'

const tabs: TargetMarketTabOption[] = [
  { value: 'sales_region', label: 'Sales regions' },
  { value: 'geographic_region', label: 'Geographic regions' },
  { value: 'country_or_territory', label: 'Countries & territories' },
  { value: 'us_state', label: 'US states' },
]
const MAX_SELECTIONS = 10

function descendants(
  market: TargetMarket,
  byId: Map<string, TargetMarket>,
  visiting: Set<string> = new Set()
): Set<string> {
  if (visiting.has(market.id)) return new Set()

  const nextVisiting = new Set(visiting)

  nextVisiting.add(market.id)

  const leaves = new Set<string>()

  if (market.memberIds.length === 0) {
    leaves.add(market.id)
    return leaves
  }

  for (const memberId of market.memberIds) {
    const member = byId.get(memberId)

    if (!member) continue

    for (const leaf of descendants(member, byId, nextVisiting)) leaves.add(leaf)
  }

  return leaves
}

function selectedOverlap(
  selected: TargetMarket[],
  byId: Map<string, TargetMarket>
): boolean {
  const coverage = selected.map((market) => descendants(market, byId))

  for (let i = 0; i < coverage.length; i += 1) {
    const first = coverage[i]

    if (!first) continue

    for (let j = i + 1; j < coverage.length; j += 1) {
      const second = coverage[j]

      if (!second) continue

      for (const leaf of first) {
        if (second.has(leaf)) return true
      }
    }
  }

  return false
}

export function TargetMarketSelect({
  id,
  catalog,
  value,
  onValueChange,
  disabled = false,
  error,
}: TargetMarketSelectProps) {
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const [activeTab, setActiveTab] = useState<TargetMarketTab>('sales_region')
  const [parentId, setParentId] = useState<string | undefined>()
  const [activeIndex, setActiveIndex] = useState(0)
  const inputRef = useRef<HTMLInputElement>(null)
  const byId = useMemo(
    () => new Map(catalog.targetMarkets.map((market) => [market.id, market])),
    [catalog.targetMarkets]
  )
  const selected = value
    .map((marketId) => byId.get(marketId))
    .filter((market): market is TargetMarket => market !== undefined)
  const global = catalog.targetMarkets.find(
    (market) => market.type === 'global'
  )
  const globalSelected = global !== undefined && value.includes(global.id)
  const visibleMarkets = useMemo(() => {
    const normalizedSearch = search.trim().toLocaleLowerCase()

    if (normalizedSearch) {
      return catalog.targetMarkets.filter((market) =>
        market.label.toLocaleLowerCase().includes(normalizedSearch)
      )
    }

    if (parentId) {
      const parent = byId.get(parentId)

      if (!parent) return []
      return parent.memberIds
        .map((memberId) => byId.get(memberId))
        .filter((market): market is TargetMarket => market !== undefined)
    }

    return catalog.targetMarkets.filter((market) => market.type === activeTab)
  }, [activeTab, byId, catalog.targetMarkets, parentId, search])

  useEffect(() => {
    setActiveIndex((index) =>
      Math.min(index, Math.max(visibleMarkets.length - 1, 0))
    )
  }, [visibleMarkets.length])

  const overlap = selectedOverlap(selected, byId)
  const countLabel = `${value.length} of ${MAX_SELECTIONS} markets selected`
  const describedBy = error ? `${id}-error` : undefined

  function toggleMarket(market: TargetMarket) {
    if (disabled) return

    const isSelected = value.includes(market.id)

    if (isSelected) {
      if (value.length === 1 && market.type !== 'global') {
        return
      }

      onValueChange(value.filter((marketId) => marketId !== market.id))

      return
    }

    if (market.type === 'global') {
      onValueChange([market.id])

      return
    }

    if (value.length >= MAX_SELECTIONS) {
      return
    }

    const withoutGlobal = global
      ? value.filter((marketId) => marketId !== global.id)
      : value

    onValueChange([...withoutGlobal, market.id])
  }

  function handleInputKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === 'Escape') {
      setOpen(false)

      return
    }

    if (event.key === 'Backspace' && search === '' && value.length > 1) {
      const lastId = value[value.length - 1]

      if (lastId) {
        onValueChange(value.slice(0, -1))
      }

      return
    }

    if (visibleMarkets.length === 0) {
      return
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault()
      setActiveIndex((index) => (index + 1) % visibleMarkets.length)
    } else if (event.key === 'ArrowUp') {
      event.preventDefault()
      setActiveIndex(
        (index) => (index - 1 + visibleMarkets.length) % visibleMarkets.length
      )
    } else if (event.key === 'Home') {
      event.preventDefault()
      setActiveIndex(0)
    } else if (event.key === 'End') {
      event.preventDefault()
      setActiveIndex(visibleMarkets.length - 1)
    } else if (event.key === 'Enter') {
      event.preventDefault()
      const activeMarket = visibleMarkets[activeIndex]

      if (activeMarket) {
        toggleMarket(activeMarket)
      }
    }
  }

  function openPopover(nextOpen: boolean) {
    setOpen(nextOpen)

    if (nextOpen) {
      setSearch('')
      setParentId(undefined)
      setActiveIndex(0)
    }
  }

  return (
    <div className="space-y-2">
      <div className="sr-only" aria-live="polite">
        {countLabel}
      </div>

      <Popover open={open} onOpenChange={openPopover}>
        <PopoverTrigger asChild>
          <button
            type="button"
            id={id}
            aria-label="Select target markets"
            disabled={disabled}
            aria-describedby={describedBy}
            aria-invalid={error ? true : undefined}
            aria-expanded={open}
            aria-haspopup="dialog"
            className={cn(
              'flex min-h-11 w-full items-center justify-between gap-2 rounded-lg border border-input bg-muted/40 px-3 py-2 text-left text-sm outline-none transition-colors focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50 disabled:cursor-not-allowed disabled:opacity-50',
              error && 'border-destructive focus-visible:ring-destructive/20'
            )}
          >
            <span
              className={
                value.length > 0 ? 'text-foreground' : 'text-muted-foreground'
              }
            >
              {value.length > 0
                ? `${value.length} markets selected`
                : 'Select target markets'}
            </span>
            <ChevronDown
              className="size-4 shrink-0 text-muted-foreground"
              aria-hidden="true"
            />
          </button>
        </PopoverTrigger>
        <PopoverContent
          align="start"
          className="max-h-(--radix-popover-content-available-height) w-[min(32rem,calc(100vw-2rem))] overflow-y-auto p-3"
          aria-label="Target markets"
          onOpenAutoFocus={(event) => {
            event.preventDefault()
            inputRef.current?.focus()
          }}
        >
          <div className="space-y-3">
            <div className="relative">
              <Search
                className="pointer-events-none absolute top-3 left-3 size-4 text-muted-foreground"
                aria-hidden="true"
              />
              <Input
                ref={inputRef}
                role="combobox"
                aria-label="Search target markets"
                aria-autocomplete="list"
                aria-controls={`${id}-options`}
                aria-activedescendant={
                  visibleMarkets[activeIndex]
                    ? `${id}-option-${visibleMarkets[activeIndex].id}`
                    : undefined
                }
                placeholder="Search target markets"
                value={search}
                disabled={disabled}
                onChange={(event) => {
                  setSearch(event.target.value)
                  setActiveIndex(0)
                }}
                onKeyDown={handleInputKeyDown}
                className="pl-9"
              />
            </div>

            {!search && (
              <div
                className="flex flex-wrap gap-1 border-b"
                role="tablist"
                aria-label="Target-market hierarchy"
              >
                {tabs.map((tab) => (
                  <button
                    key={tab.value}
                    type="button"
                    role="tab"
                    aria-selected={activeTab === tab.value}
                    disabled={disabled}
                    className={cn(
                      'rounded-t-md px-2 py-1.5 text-xs font-medium text-muted-foreground outline-none transition-colors hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring',
                      activeTab === tab.value &&
                        'border-b-2 border-foreground text-foreground'
                    )}
                    onClick={() => {
                      setActiveTab(tab.value)
                      setParentId(undefined)
                      setActiveIndex(0)
                    }}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
            )}

            {parentId && !search && (
              <button
                type="button"
                disabled={disabled}
                className="inline-flex items-center gap-1 text-xs font-medium text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                onClick={() => setParentId(byId.get(parentId)?.parentId)}
              >
                <ChevronLeft className="size-3.5" aria-hidden="true" />
                Back to hierarchy
              </button>
            )}

            {global && (
              <>
                <div className="flex items-center gap-2 rounded-md bg-muted/50 px-2 py-1.5 text-sm">
                  <Checkbox
                    id={`${id}-${global.id}`}
                    aria-label={global.label}
                    checked={globalSelected}
                    disabled={disabled}
                    onCheckedChange={(checked) => {
                      if (checked === true || checked === false) {
                        toggleMarket(global)
                      }
                    }}
                  />
                  <label
                    htmlFor={`${id}-${global.id}`}
                    className="min-w-0 flex-1 cursor-pointer truncate font-medium"
                  >
                    {global.label}
                  </label>
                </div>
                <div role="separator" className="border-t" />
              </>
            )}

            <ScrollArea className="h-56">
              <div
                id={`${id}-options`}
                role="listbox"
                aria-label="Target-market options"
                className="space-y-1 pr-2"
              >
                {visibleMarkets.map((market, index) => {
                  const isSelected = value.includes(market.id)
                  const selectionLimitReached =
                    value.length >= MAX_SELECTIONS && !isSelected

                  return (
                    <div
                      key={market.id}
                      id={`${id}-option-${market.id}`}
                      role="option"
                      aria-selected={isSelected}
                      className={cn(
                        'flex items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-muted',
                        activeIndex === index && 'bg-muted'
                      )}
                    >
                      <Checkbox
                        id={`${id}-${market.id}`}
                        aria-label={market.label}
                        checked={isSelected}
                        disabled={disabled || selectionLimitReached}
                        onCheckedChange={(checked) => {
                          if (checked === true || checked === false) {
                            toggleMarket(market)
                          }
                        }}
                      />
                      <label
                        htmlFor={`${id}-${market.id}`}
                        className="min-w-0 flex-1 cursor-pointer truncate"
                      >
                        {market.label}
                      </label>
                      {market.memberIds.length > 0 && (
                        <button
                          type="button"
                          aria-label={`Browse ${market.label}`}
                          disabled={disabled}
                          className="rounded p-1 text-muted-foreground outline-none hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring"
                          onClick={() => {
                            setSearch('')
                            setParentId(market.id)
                            setActiveIndex(0)
                            inputRef.current?.focus()
                          }}
                        >
                          <ChevronRight className="size-4" aria-hidden="true" />
                        </button>
                      )}
                    </div>
                  )
                })}
                {visibleMarkets.length === 0 && (
                  <p className="px-2 py-4 text-center text-sm text-muted-foreground">
                    No markets found.
                  </p>
                )}
              </div>
            </ScrollArea>

            <div className="space-y-1 text-xs text-muted-foreground">
              <p>{countLabel}</p>
              {globalSelected && <p>Global includes all markets.</p>}
              {!globalSelected && overlap && (
                <p>Some selected markets overlap.</p>
              )}
              {value.length >= MAX_SELECTIONS && (
                <p>Maximum of {MAX_SELECTIONS} markets selected.</p>
              )}
            </div>
          </div>
        </PopoverContent>
      </Popover>

      {selected.length > 0 && (
        <div
          className="flex flex-wrap gap-1.5"
          aria-label="Selected target markets"
        >
          {selected.map((market) => (
            <span
              key={market.id}
              className="inline-flex items-center gap-1 rounded-full bg-secondary px-2.5 py-1 text-xs font-medium text-secondary-foreground"
            >
              {market.label}
              <button
                type="button"
                aria-label={`Remove ${market.label}`}
                disabled={disabled}
                className="rounded-full outline-none hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring"
                onClick={() => toggleMarket(market)}
              >
                <X className="size-3" aria-hidden="true" />
              </button>
            </span>
          ))}
        </div>
      )}

      {error && (
        <p id={`${id}-error`} className="text-sm text-destructive">
          {error}
        </p>
      )}
    </div>
  )
}
